/**
 * "Is this recall finished?" — asked of the record that cannot be destroyed.
 *
 * ── Why this file exists (P4) ──
 *
 * On 2026-09-08 a recall completed on 31 August was re-selected into the work
 * pool and re-assigned to the SAME technician who had completed it. Five guards
 * should have stopped that. Every one was open, and the instructive part is why:
 *
 *   - `completed_recalls` arm 1 keys on `assignment_status`. The completing
 *     assignment row had already been deleted, so it saw nothing.
 *   - `checkExistingAssignment` also reads `technician_assignments`, and
 *     deliberately filters to non-terminal statuses. Row gone, and terminal
 *     rows excluded by design — it reported "no existing assignment", so a
 *     fresh one was created.
 *
 * Both guards failed for the same reason: they read evidence that an earlier
 * bug destroys. A check is only as durable as the row it reads.
 *
 * `recall_lifecycle_records` is the one row that survives. The `authenticated`
 * role holds SELECT, INSERT and UPDATE on it but NOT DELETE — which is why, in
 * the whole history of this system, no lifecycle record has ever been destroyed
 * while 1,751 completion records have. So every terminal check reads the
 * lifecycle record FIRST, and treats the assignment row as corroboration only.
 *
 * That ordering is the entire point. Reversing it re-creates the incident.
 */

import { getProdSupabaseClient } from '@/lib/supabase/client';
import { getTenantIdFromSession } from '@/lib/supabase/tenantScoping';
import { isTerminalState } from '@/lib/supabase/lifecycleState';
import { isFinalAssignmentStatus } from '@/lib/supabase/assignmentStatus';
import {
  LifecycleRefusalError,
  type LifecycleRefusalEvidence
} from '@/lib/errors/lifecycleRefusal';
import { logSystemError } from '@/lib/utils/errorTranslator';

/** What the two sources together say about a recall's fate. */
export interface TerminalVerdict {
  /** True when either source says this recall is finished. */
  isTerminal: boolean;
  /** `completed` vs `cancelled` — drives which refusal copy the user sees. */
  outcome?: 'completed' | 'cancelled';
  /** Who finished it and when, so the refusal can say so. */
  evidence: LifecycleRefusalEvidence;
  /**
   * True when we could not read the state at all.
   *
   * Distinct from `isTerminal: false`, and the distinction is user-visible: a
   * failed check must read as "we couldn't tell", never as "you may not". Users
   * who are told the wrong one file bugs against the wrong thing.
   */
  checkFailed: boolean;
}

const NOT_TERMINAL: TerminalVerdict = { isTerminal: false, evidence: {}, checkFailed: false };

interface LifecycleEvidenceRow {
  lifecycle_state?: string | null;
  final_status?: string | null;
  completed_at?: string | null;
  serviced_by_technician_name?: string | null;
}

interface AssignmentEvidenceRow {
  assignment_status?: string | null;
  completed_at?: string | null;
}

/**
 * Read the durable record. This is the PRIMARY source and must stay so.
 *
 * It answers terminal on either of two independent signals:
 *
 *   - `lifecycle_state IN ('completed','cancelled')` — the fast path, and the
 *     one the recall list RPC uses.
 *   - `final_status = 'completed' AND completed_at IS NOT NULL`, or
 *     `final_status = 'vehicle_sold'` — evidence that survives even when the
 *     state column itself was clobbered, which is the case for 4,473 and 846
 *     rows respectively.
 *
 * Two signals rather than one because the state column is exactly what the
 * defects corrupt; a guard resting on it alone inherits the corruption.
 *
 * Deliberately NOT included: the recycle family (`incomplete`, `not_available`,
 * `other` without a completed_at). Those are 635 rows that MUST stay actionable
 * — a recycled recall is back in the pool and has to be assignable again.
 * Treating them as terminal would hide live work, which is the worse failure.
 */
function readLifecycleEvidence(row: LifecycleEvidenceRow | null): TerminalVerdict | null {
  if (!row) return null;

  const finalStatus = row.final_status ?? undefined;
  const evidence: LifecycleRefusalEvidence = {
    ...(row.completed_at ? { completedAt: row.completed_at } : {}),
    ...(row.serviced_by_technician_name ? { completedBy: row.serviced_by_technician_name } : {}),
    ...(finalStatus ? { finalStatus } : {})
  };

  if (row.lifecycle_state === 'completed') {
    return { isTerminal: true, outcome: 'completed', evidence, checkFailed: false };
  }
  if (row.lifecycle_state === 'cancelled') {
    return { isTerminal: true, outcome: 'cancelled', evidence, checkFailed: false };
  }

  // The state was clobbered but the outcome columns survived.
  if (finalStatus === 'completed' && row.completed_at) {
    return { isTerminal: true, outcome: 'completed', evidence, checkFailed: false };
  }
  if (finalStatus === 'vehicle_sold') {
    return { isTerminal: true, outcome: 'cancelled', evidence, checkFailed: false };
  }

  return null;
}

/**
 * Ask whether a recall has reached a terminal state.
 *
 * Never throws. Callers get a verdict, including "we could not tell", and decide
 * how loudly to react — which is what lets a destructive action fail closed
 * while a read-only surface degrades quietly.
 */
export async function checkRecallTerminal(
  vin: string,
  campaignNumber: string,
  tenantIdOverride?: string
): Promise<TerminalVerdict> {
  try {
    const prodClient = getProdSupabaseClient();
    const tenantId = tenantIdOverride ?? (await getTenantIdFromSession(prodClient));
    if (!tenantId) {
      return { isTerminal: false, evidence: {}, checkFailed: true };
    }

    // PRIMARY: the record that cannot be deleted.
    const { data: lifecycleData, error: lifecycleError } = await prodClient
      .from('recall_lifecycle_records')
      .select('lifecycle_state, final_status, completed_at, serviced_by_technician_name')
      .eq('vin', vin)
      .eq('campaign_number', campaignNumber)
      .eq('tenant_id', tenantId)
      .maybeSingle();

    if (lifecycleError) {
      logSystemError(lifecycleError, 'checking whether a recall is already finished');
      return { isTerminal: false, evidence: {}, checkFailed: true };
    }

    const verdict = readLifecycleEvidence(lifecycleData as LifecycleEvidenceRow | null);
    if (verdict) return verdict;

    // CORROBORATION ONLY. Reached when the lifecycle record says nothing —
    // typically a recall assigned before lifecycle tracking existed, whose
    // assignment row still carries the outcome. Never the primary source.
    return await corroborateFromAssignment(vin, campaignNumber, tenantId);
  } catch (error) {
    logSystemError(error, 'checking whether a recall is already finished');
    return { isTerminal: false, evidence: {}, checkFailed: true };
  }
}

async function corroborateFromAssignment(
  vin: string,
  campaignNumber: string,
  tenantId: string
): Promise<TerminalVerdict> {
  const prodClient = getProdSupabaseClient();
  const { data, error } = await prodClient
    .from('technician_assignments')
    .select('assignment_status, completed_at')
    .eq('vin', vin)
    .eq('campaign_number', campaignNumber)
    .eq('tenant_id', tenantId)
    .in('assignment_status', ['completed', 'cancelled', 'not_there'])
    .limit(1);

  if (error) {
    logSystemError(error, 'confirming whether a recall is already finished');
    return { isTerminal: false, evidence: {}, checkFailed: true };
  }

  const rows = (data ?? []) as AssignmentEvidenceRow[];
  const row = rows[0];
  if (!row || !isFinalAssignmentStatus(row.assignment_status)) return NOT_TERMINAL;

  return {
    isTerminal: true,
    outcome: row.assignment_status === 'completed' ? 'completed' : 'cancelled',
    evidence: row.completed_at ? { completedAt: row.completed_at } : {},
    checkFailed: false
  };
}

/**
 * Refuse the action if the recall is finished, or if we could not find out.
 *
 * Failing closed on a check failure is the right default for anything that
 * assigns, re-assigns or removes: the cost of a wrong refusal is one retry, the
 * cost of a wrong permission is a technician driving to a car that is already
 * fixed. The copy distinguishes the two cases so the refusal is honest about
 * which one happened.
 */
export async function assertRecallNotTerminal(
  vin: string,
  campaignNumber: string,
  tenantIdOverride?: string
): Promise<void> {
  const verdict = await checkRecallTerminal(vin, campaignNumber, tenantIdOverride);

  if (verdict.checkFailed) {
    throw new LifecycleRefusalError('status_check_failed');
  }
  if (!verdict.isTerminal) return;

  throw new LifecycleRefusalError(
    verdict.outcome === 'completed' ? 'recall_completed' : 'recall_cancelled',
    verdict.evidence
  );
}

/**
 * The same question answered from data already in hand, with no round trip.
 *
 * Used for INSTANT refusals. `.claude/rules/instant-feedback.md` requires a
 * perceived response under 16ms, and the house pattern gets there by closing the
 * dialog and toasting success BEFORE the write. For a terminal-state action that
 * inverts badly — the user sees "Removed", then it reverses.
 *
 * So the check runs locally against already-loaded state, and the server guard
 * plus the database trigger are the backstop rather than the primary check.
 * That keeps the feedback instant AND keeps the refusal instant.
 */
export function isRecallTerminalLocally(recall: {
  lifecycle_state?: string | null;
  final_status?: string | null;
  completed_at?: string | null;
  assignment_status?: string | null;
}): boolean {
  if (isTerminalState(recall.lifecycle_state)) return true;
  if (isFinalAssignmentStatus(recall.assignment_status)) return true;
  if (recall.final_status === 'completed' && recall.completed_at) return true;
  if (recall.final_status === 'vehicle_sold') return true;
  return false;
}
